import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import getHelpCenterUrl from '@alltrails/core/utils/getHelpCenterUrl';
import Heading from '@alltrails/denali/components/Heading';
import Link from '@alltrails/denali/components/Link';
import Expandable from './Expandable';
import styles from './FrequentlyAskedQuestions.module.scss';

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=586%3A30927
const FrequentlyAskedQuestions = () => {
  const languageRegionCode = useLanguageRegionCode();

  const questions = useMemo(
    () => [
      {
        answer: (
          <FormattedMessage defaultMessage="AllTrails gift memberships are delivered by email. They contain a redemption code and link to redeem on the web. When redeemed, the subscription will be applied to your account." />
        ),
        question: <FormattedMessage defaultMessage="How do I redeem my subscription?" />
      },
      {
        answer: (
          <FormattedMessage
            defaultMessage="Not at this time.<br></br><br></br>Gift subscriptions can't be added on top of an existing subscription.<br></br><br></br>If your recipient already has an active subscription, we'd recommend waiting until it's set to expire before redeeming the gift, so they don't lose any unused time."
            values={{
              br: () => <br />
            }}
          />
        ),
        question: <FormattedMessage defaultMessage="Can I give a subscription to someone who already has a Plus or Peak account?" />
      },
      {
        answer: <FormattedMessage defaultMessage="Absolutely. We'll send you an email notification as soon as the gift email is opened." />,
        question: <FormattedMessage defaultMessage="Will I know if my recipient receives their gift?" />
      },
      {
        answer: (
          <FormattedMessage
            defaultMessage="We can help! Please send your order confirmation number to <a>support@alltrails.com</a> and our customer support team will get to the bottom of it."
            values={{
              a: chunks => <a href={`mailto:${chunks}`}>{chunks}</a>
            }}
          />
        ),
        question: <FormattedMessage defaultMessage="The subscription I sent wasn’t received?" />
      },
      {
        answer: (
          <FormattedMessage defaultMessage="Yes, your recipient will have 24 months to redeem their subscription from the moment they receive the gift. Once redeemed, the code is good for the length of the subscription that was gifted. For example, if you gift someone a year subscription, that year begins whenever they redeem it." />
        ),
        question: <FormattedMessage defaultMessage="Is there an expiration date on the subscription?" />
      },
      {
        answer: (
          <FormattedMessage defaultMessage="If you don't want your gift delivered by email, you'll have the option to print instead. Then you can deliver your gift by hand or by mail and the PDF will include all the information your recipient needs to redeem it." />
        ),
        question: <FormattedMessage defaultMessage="Can I buy a physical gift subscription?" />
      }
    ],
    []
  );

  return (
    <section className={styles.container}>
      <Heading as="h2" size="600">
        <FormattedMessage defaultMessage="Frequently asked questions" />
      </Heading>
      <div className={styles.questions}>
        {questions.map((question, index) => (
          <Expandable key={index} title={question.question}>
            <Typography className={styles.answer} component="div" variant="text200">
              {question.answer}
            </Typography>
          </Expandable>
        ))}
      </div>
      <Typography className={styles.need} color="secondary" component="div" variant="text100">
        <FormattedMessage
          defaultMessage="Need help? Ask at <a>support@alltrails.com</a>"
          values={{
            a: chunks => (
              <Link href={`mailto:${chunks}`} variant="secondary">
                {chunks}
              </Link>
            )
          }}
        />
        <br />
        <FormattedMessage
          defaultMessage="or visit our <a>Help Center</a>"
          values={{
            a: chunks => (
              <Link href={getHelpCenterUrl(languageRegionCode)} variant="secondary">
                {chunks}
              </Link>
            )
          }}
        />
      </Typography>
    </section>
  );
};

export default FrequentlyAskedQuestions;
